import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { normalizeNames } from './channelNames'
import { useZelloRx } from './rxAudio'
import { useZelloTransport } from './transport'
import { useZelloTx } from './txAudio'
import type { ChannelApiEvent, UseZelloOpts, ZelloState } from './types'

export type { ChannelApiEvent, UseZelloOpts, ZelloState } from './types'

// Soft cap on how many channels we logon to at once.
const LOGON_CHANNEL_CAP = 50

// Single-WebSocket Zello manager. Zello Work allows only one concurrent
// session per user, so we share one WS across all subscribed channels and
// use the documented `channels: [...]` array logon (spec §75) to subscribe
// to all of them at once.
export function useZello({
  serverUrl,
  username,
  password,
  authToken,
  language = 'en',
}: UseZelloOpts) {
  const [state, setState] = useState<ZelloState>({
    status: 'idle',
    rx: 'idle',
    tx: 'idle',
    sdkReady: true,
    readyChannels: [],
  })

  const desiredRef = useRef<string[]>([])
  const loggedOnChannelsRef = useRef<Set<string>>(new Set())
  const activeTxChannelRef = useRef<string | null>(null)

  const emitRef = useRef<((e: ChannelApiEvent) => void) | null>(null)
  const setOnEvent = useCallback((fn: ((e: ChannelApiEvent) => void) | null) => {
    emitRef.current = fn
  }, [])
  const emit = useCallback((e: ChannelApiEvent) => {
    emitRef.current?.(e)
  }, [])

  const rx = useZelloRx({ emit, setState })

  const {
    handleJsonMessage,
    handleBinaryMessage,
    stopAllRx,
    resumeAudio,
    setStreamVolume,
    isChannelRxEnabled,
    setChannelRxEnabled,
    toggleChannelRxEnabled,
    setRxEnabledChannels,
    getRxEnabledChannels,
  } = rx

  const transport = useZelloTransport({
    serverUrl,
    username,
    password,
    authToken,
    language,
    emit,
    setState,
    onJsonMessage: handleJsonMessage,
    onBinaryMessage: handleBinaryMessage,
    onBeforeDisconnect: stopAllRx,
  })

  const {
    wsRef,
    seqRef,
    isLoggedOnRef,
    safeSend,
    safeSendBinary,
    closeWs,
    ensureConnectedAndLoggedOn,
    waitForChannelReady,
  } = transport

  // Manual recovery — fired when the user clicks the Reconnect button in
  // the kicked banner or in a "Connection failed" PTT surface. Clears
  // connectionFailed up-front so the UI reverts from the failed state
  // immediately, then closes the WS and re-logons. If the new attempt
  // also stalls, the watchdog will re-set connectionFailed at the 30s
  // mark (closeWs intentionally does NOT clear that flag, so the
  // watchdog-driven failed state survives WS teardown).
  const forceReconnect = useCallback(async () => {
    const channels = Array.from(loggedOnChannelsRef.current)
    if (!channels.length) return
    setState(p => (p.connectionFailed ? { ...p, connectionFailed: false } : p))
    closeWs()
    await ensureConnectedAndLoggedOn(channels)
  }, [closeWs, ensureConnectedAndLoggedOn])

  const tx = useZelloTx({
    authToken,
    seqRef,
    wsRef,
    isLoggedOnRef,
    activeTxChannelRef,
    safeSend,
    safeSendBinary,
    setState,
    waitForChannelReady,
    forceReconnect,
  })

  const { startTalking, stopTalking } = tx

  const setActiveChannel = useCallback((channel: string | null) => {
    const next = (channel || '').trim() || null
    if (activeTxChannelRef.current === next) return
    activeTxChannelRef.current = next
    setState(p => (p.channel === next ? p : { ...p, channel: next || undefined }))
  }, [])

  const connect = useCallback(async (channelOrChannels: string | string[]) => {
    const desired = normalizeNames(Array.isArray(channelOrChannels) ? channelOrChannels : [channelOrChannels])
    desiredRef.current = desired

    if (!desired.length) return

    // Re-logon with EXACTLY the desired set — no union with previously
    // subscribed channels. The caller (VoiceProvider) computes the right
    // set including/excluding Everyone, listening list, exclusive channel,
    // etc. Unioning would defeat exclusive mode (alert drawer) and stale
    // toggled-off channels would never get unsubscribed.
    const desiredSet = new Set(desired)
    const same =
      desiredSet.size === loggedOnChannelsRef.current.size &&
      desired.every(ch => loggedOnChannelsRef.current.has(ch))
    if (same && isLoggedOnRef.current) return

    let logonList = desired
    if (logonList.length > LOGON_CHANNEL_CAP) {
      console.warn('[Zello] logon channel count exceeds cap, truncating', {
        count: logonList.length,
        cap: LOGON_CHANNEL_CAP,
      })
      logonList = logonList.slice(0, LOGON_CHANNEL_CAP)
    }

    if (isLoggedOnRef.current) closeWs()
    loggedOnChannelsRef.current = new Set(logonList)
    await ensureConnectedAndLoggedOn(logonList)
  }, [closeWs, ensureConnectedAndLoggedOn, isLoggedOnRef])

  const disconnect = useCallback(async () => {
    loggedOnChannelsRef.current = new Set()
    desiredRef.current = []
    closeWs()
  }, [closeWs])

  const disconnectAll = useCallback(async () => {
    await disconnect()
  }, [disconnect])

  const canPTT =
    state.status === 'connected' &&
    state.tx === 'idle' &&
    !!activeTxChannelRef.current &&
    state.readyChannels.includes(activeTxChannelRef.current.toLowerCase())

  // Pause TX on tab hide.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) void stopTalking()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [stopTalking])

  // Tear down the WS on unmount.
  useEffect(() => {
    return () => { closeWs() }
  }, [closeWs])

  const api = useMemo(() => ({
    state,
    connect,
    disconnect,
    disconnectAll,
    setActiveChannel,
    setOnEvent,
    resumeAudio,
    startTalking,
    stopTalking,
    forceReconnect,
    canPTT,

    setStreamVolume,

    isChannelRxEnabled,
    setChannelRxEnabled,
    toggleChannelRxEnabled,
    setRxEnabledChannels,
    getRxEnabledChannels,
  }), [
    state,
    connect,
    disconnect,
    disconnectAll,
    setActiveChannel,
    setOnEvent,
    resumeAudio,
    startTalking,
    stopTalking,
    forceReconnect,
    canPTT,
    setStreamVolume,
    isChannelRxEnabled,
    setChannelRxEnabled,
    toggleChannelRxEnabled,
    setRxEnabledChannels,
    getRxEnabledChannels,
  ])

  return api
}
